import { KeywordWithIntent, CopyOrigin, Product, ViewType } from '../types';

/**
 * Utility functions for copying keywords across products, brands, and sub-brands
 */

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Creates a copy of a keyword with copy origin metadata
 */
export function createKeywordCopy(
  keyword: KeywordWithIntent,
  viewType: ViewType,
  sourceId: string,
  sourceName: string
): KeywordWithIntent {
  const copyOrigin: CopyOrigin = {
    viewType,
    sourceId,
    sourceName,
    copiedAt: new Date()
  };

  return {
    ...keyword,
    copiedFrom: copyOrigin
  };
}

/**
 * Checks if a keyword is a copied keyword
 */
export function isCopiedKeyword(keyword: KeywordWithIntent): boolean {
  return !!keyword.copiedFrom;
}

/**
 * Checks if a keyword text already exists in a product's keywords
 * (checks both text and intent to be thorough - same text with different intent is allowed)
 */
export function keywordExistsInProduct(
  keywordText: string,
  keywordIntent: string,
  product: Product
): boolean {
  return product.keywords.some(
    k => k.text === keywordText && k.intent === keywordIntent
  );
}

/**
 * Checks if a keyword exists anywhere in an array of products (for brand/sub-brand checks)
 */
export function keywordExistsInProducts(
  keywordText: string,
  keywordIntent: string,
  products: Product[]
): boolean {
  return products.some(product => 
    keywordExistsInProduct(keywordText, keywordIntent, product)
  );
}

/**
 * Gets all products that belong to a specific brand
 */
export function getProductsByBrand(brandName: string, allProducts: Product[]): Product[] {
  return allProducts.filter(p => p.brand === brandName);
}

/**
 * Gets all products that belong to a specific sub-brand
 */
export function getProductsBySubBrand(subBrandName: string, allProducts: Product[]): Product[] {
  return allProducts.filter(p => p.subBrand === subBrandName);
}

// ============================================================================
// COPY OPERATIONS
// ============================================================================

/**
 * Copies a keyword to a specific product
 * Returns the updated product
 */
export function copyKeywordToProduct(
  keyword: KeywordWithIntent,
  sourceProductId: string,
  sourceName: string,
  sourceViewType: ViewType,
  targetProduct: Product,
  copySelectionState: boolean = false
): Product {
  // Check if keyword already exists
  if (keywordExistsInProduct(keyword.text, keyword.intent, targetProduct)) {
    throw new Error(`Keyword "${keyword.text}" already exists in ${targetProduct.name}`);
  }

  // Create the keyword copy
  const keywordCopy = createKeywordCopy(
    keyword,
    sourceViewType,
    sourceProductId,
    sourceName
  );

  // Add to target product's keywords
  const updatedKeywords = [...targetProduct.keywords, keywordCopy];

  // Handle selection state
  let updatedSelectedKeywords = [...targetProduct.selectedKeywords];
  if (copySelectionState) {
    // If the source keyword was selected, also select the copy
    updatedSelectedKeywords.push(keyword.text);
  }

  return {
    ...targetProduct,
    keywords: updatedKeywords,
    selectedKeywords: updatedSelectedKeywords
  };
}

/**
 * Copies a keyword from one product to multiple target products
 * Returns array of updated products (only the ones that were modified)
 */
export function copyKeywordToMultipleProducts(
  keyword: KeywordWithIntent,
  sourceProductId: string,
  sourceName: string,
  sourceViewType: ViewType,
  targetProducts: Product[],
  sourceProductIsSelected: boolean
): {
  updatedProducts: Product[];
  skippedProducts: { product: Product; reason: string }[];
} {
  const updatedProducts: Product[] = [];
  const skippedProducts: { product: Product; reason: string }[] = [];

  targetProducts.forEach(targetProduct => {
    try {
      const updatedProduct = copyKeywordToProduct(
        keyword,
        sourceProductId,
        sourceName,
        sourceViewType,
        targetProduct,
        sourceProductIsSelected
      );
      updatedProducts.push(updatedProduct);
    } catch (error) {
      skippedProducts.push({
        product: targetProduct,
        reason: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  });

  return { updatedProducts, skippedProducts };
}

// ============================================================================
// REMOVE COPY OPERATIONS
// ============================================================================

/**
 * Removes a copied keyword from a product
 * Only works on keywords that have copiedFrom metadata
 * Also removes from selectedKeywords if it was selected
 */
export function removeCopiedKeyword(
  keywordText: string,
  fromProduct: Product
): Product {
  // Find the keyword
  const keywordToRemove = fromProduct.keywords.find(k => k.text === keywordText);
  
  if (!keywordToRemove) {
    throw new Error(`Keyword "${keywordText}" not found in ${fromProduct.name}`);
  }

  if (!isCopiedKeyword(keywordToRemove)) {
    throw new Error(`Keyword "${keywordText}" is not a copied keyword and cannot be removed this way`);
  }

  // Remove from keywords array
  const updatedKeywords = fromProduct.keywords.filter(k => k.text !== keywordText);

  // Remove from selectedKeywords if it was selected
  const updatedSelectedKeywords = fromProduct.selectedKeywords.filter(k => k !== keywordText);

  return {
    ...fromProduct,
    keywords: updatedKeywords,
    selectedKeywords: updatedSelectedKeywords
  };
}

// ============================================================================
// VALIDATION & AVAILABILITY CHECK
// ============================================================================

/**
 * Interface for checking copy availability across products
 */
export interface CopyAvailability {
  productId: string;
  productName: string;
  available: boolean;
  reason?: string;
}

/**
 * Checks which products can receive a keyword copy
 * Used for displaying available targets in the UI
 */
export function checkCopyAvailability(
  keyword: KeywordWithIntent,
  targetProducts: Product[],
  sourceProductId?: string
): CopyAvailability[] {
  return targetProducts.map(product => {
    // Can't copy to itself
    if (product.id === sourceProductId) {
      return {
        productId: product.id,
        productName: product.name,
        available: false,
        reason: 'Cannot copy to source product'
      };
    }

    // Check if keyword already exists
    if (keywordExistsInProduct(keyword.text, keyword.intent, product)) {
      return {
        productId: product.id,
        productName: product.name,
        available: false,
        reason: 'Keyword already exists'
      };
    }

    return {
      productId: product.id,
      productName: product.name,
      available: true
    };
  });
}

/**
 * Groups products by brand for brand view
 * Returns a map of brandName -> Product[]
 */
export function groupProductsByBrand(products: Product[]): Map<string, Product[]> {
  const groups = new Map<string, Product[]>();
  
  products.forEach(product => {
    const brandName = product.brand || 'Unknown Brand';
    if (!groups.has(brandName)) {
      groups.set(brandName, []);
    }
    groups.get(brandName)!.push(product);
  });
  
  return groups;
}

/**
 * Groups products by sub-brand for sub-brand view
 * Returns a map of subBrandName -> Product[]
 */
export function groupProductsBySubBrand(products: Product[]): Map<string, Product[]> {
  const groups = new Map<string, Product[]>();
  
  products.forEach(product => {
    const subBrandName = product.subBrand || 'Unknown Sub-Brand';
    if (!groups.has(subBrandName)) {
      groups.set(subBrandName, []);
    }
    groups.get(subBrandName)!.push(product);
  });
  
  return groups;
}

/**
 * Checks if a keyword exists in a brand (any product within that brand)
 */
export function keywordExistsInBrand(
  keywordText: string,
  keywordIntent: string,
  brandName: string,
  allProducts: Product[]
): boolean {
  const brandProducts = getProductsByBrand(brandName, allProducts);
  return keywordExistsInProducts(keywordText, keywordIntent, brandProducts);
}

/**
 * Checks if a keyword exists in a sub-brand (any product within that sub-brand)
 */
export function keywordExistsInSubBrand(
  keywordText: string,
  keywordIntent: string,
  subBrandName: string,
  allProducts: Product[]
): boolean {
  const subBrandProducts = getProductsBySubBrand(subBrandName, allProducts);
  return keywordExistsInProducts(keywordText, keywordIntent, subBrandProducts);
}
